import { CELLARS } from '@somnhold/content';
import { bellTrophy, type BattleState, type CampaignState, heroName } from '@somnhold/sim';
import { audio } from '../audio/sfx.ts';
import { t } from '../i18n.ts';
import { clear, h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import { CELLARS_LOST, CELLARS_OPENING, CELLARS_WON, type CellarsLine } from './cellars-texts.ts';
import { incursionResult, type Before } from './incursion-result.ts';
import { portrait } from './saga-panel.ts';
import type { CampaignService } from './service.ts';

/*
 * The garrison cellars around the fight (docs/EXPEDICIONES.md): Hilde's scene by the well before going down,
 * and coming back up, the closing scene and then what each one won.
 */

export interface SceneAction {
  label: string;
  test: string;
  ghost?: boolean;
  run: () => void;
}

/**
 * A short scene, full screen, line by line (the saga's look): who speaks, the line, «Next»; the last line
 * shows the actions. Tapping the text moves on too; «Back» goes a line back. The cross takes the last action (going nowhere).
 */
export const linesScene = (lines: CellarsLine[], actions: SceneAction[], vars: Record<string, string | number> = {}, kicker = t('exp.title'), start = 0): HTMLElement => {
  let i = Math.max(0, Math.min(start, lines.length - 1));
  const face = h('div.scene-face');
  const name = h('div.scene-name');
  const text = h('p.scene-text');
  const acts = h('div.scene-choices.exp-acts');
  const next = h('button.btn.ghost.scene-next', { 'data-test': 'scene-next', onclick: () => step() }, t('saga.next'));
  const prev = h('button.btn.ghost.scene-prev', { 'data-test': 'scene-prev', onclick: () => back() }, t('saga.prev'));
  const finish = (a: SceneAction): void => {
    audio.play('click');
    el.remove();
    a.run();
  };
  const el = h(
    'div.scene.exp-scene',
    { role: 'dialog', 'aria-modal': 'true', 'data-test': 'exp-scene', onclick: (ev: Event) => { if (ev.target === el || ev.target === text) step(); } },
    h('div.scene-card', {}, h('span.scene-kicker', {}, kicker), h('div.scene-who', {}, face, name), text, acts, h('div.scene-nav', {}, prev, next)),
    h('button.iconbtn.scene-close', { 'aria-label': t('saga.later'), onclick: () => finish(actions[actions.length - 1] as SceneAction), html: ICONS.close }),
  );
  const show = (): void => {
    const l = lines[i];
    if (!l) return;
    clear(face);
    face.append(portrait(l.who));
    name.textContent = l.who === 'narrator' ? '' : t(`npc.${l.who}`);
    el.classList.toggle('narrator', l.who === 'narrator');
    text.textContent = t(l.key, vars);
    text.classList.remove('in');
    void text.offsetWidth;
    text.classList.add('in');
    const last = i === lines.length - 1;
    next.classList.toggle('hidden', last);
    prev.classList.toggle('hidden', i === 0);
    clear(acts);
    if (last) for (const a of actions) acts.append(h(`button.btn${a.ghost ? '.ghost' : ''}`, { 'data-test': a.test, onclick: () => finish(a) }, a.label));
  };
  const step = (): void => {
    if (i >= lines.length - 1) return;
    i++;
    show();
  };
  const back = (): void => {
    if (i <= 0) return;
    i--;
    show();
  };
  show();
  return el;
};

/** Before going down: Hilde by the courtyard well. «Go down» opens the waiting room; «Not now» stays in the bastion. */
export const cellarsOpening = (onGo: () => void, onLater: () => void): HTMLElement =>
  linesScene(CELLARS_OPENING, [
    { label: t('exp.scene.go'), test: 'exp-go-down', run: onGo },
    { label: t('exp.scene.later'), test: 'exp-later', ghost: true, run: onLater },
  ]);

/** Why the run was lost, and who it was if someone dropped out. */
const lostWhy = (b: BattleState, names: Record<string, string>): { why: 'ink' | 'fallen' | 'gone' | 'other'; name: string } => {
  const x = b.dungeon?.exp;
  const why = x?.lost ?? 'other';
  if (why !== 'gone') return { why, name: '' };
  const i = x?.goneAt.findIndex((g) => g > 0) ?? -1;
  const hero = b.heroes[i];
  return { why, name: hero ? names[hero.pid] || hero.name : t('exp.someone') };
};

/**
 * Back up from the cellars: the closing scene (the bell in the bastion's bell tower, or why they came up without
 * it), then the end card with my haul and a line per hero with what each one won. `chestBefore`: who had already
 * opened today's chest before going down.
 */
export const cellarsEnd = (svc: CampaignService, b: BattleState, before: Before, chestBefore: Set<string>, onBack: () => void): HTMLElement => {
  const won = b.result === 'won';
  const names = Object.fromEntries(svc.state.players.map((p) => [p.id, heroName(p)]));
  const { why, name } = lostWhy(b, names);
  const lines = won ? CELLARS_WON : CELLARS_LOST[why];
  const card = (): HTMLElement => {
    const x = b.dungeon?.exp;
    const locks = x ? x.devices.filter((d) => d.kind === 'lock' && d.done).length : 0;
    const day = svc.day();
    const rows = b.heroes.map((hero) => {
      const p = svc.state.players.find((q) => q.id === hero.pid);
      const who = names[hero.pid] || hero.name;
      const chest = won && !chestBefore.has(hero.pid) && p?.expeditionDay === day;
      if (chest) return h('li.up', {}, h('span', { html: ICONS.chest }), t('exp.end.chest', { name: who, xp: CELLARS.reward.xp }));
      const xp = locks * CELLARS.xpPerLock;
      const text = xp > 0 ? t('exp.end.locksXp', { name: who, xp }) : t('exp.end.nothing', { name: who });
      return h('li', {}, h('span', { html: ICONS.hero }), won && chestBefore.has(hero.pid) ? `${text} ${t('exp.end.taken')}` : text);
    });
    const trophy = svc.state.expeditions?.cellars;
    const extra: HTMLElement[] = [h('div.exp-end-party', { 'data-test': 'exp-end-party' }, h('h4', {}, t('exp.end.party')), h('ul.ie-lines', {}, ...rows))];
    if (won && trophy) extra.push(h('p.small.ie-note', {}, t('exp.end.trophy', { n: trophy.wins, f: trophy.flawless })));
    const sub = won ? t('exp.end.wonSub') : t(`exp.end.why.${why}`, { name });
    return incursionResult(svc.state, svc.me, before, { won, place: t('exp.title'), onBack, heading: { title: won ? t('exp.end.won') : t('exp.end.lost'), sub }, extra });
  };
  // The scene sits where the card will: the card takes its place once it has been read.
  const holder = h('div.exp-end');
  holder.append(
    linesScene(
      lines,
      [
        {
          label: t('exp.end.see'),
          test: 'exp-end-see',
          run: () => holder.append(card()),
        },
      ],
      { name },
    ),
  );
  return holder;
};

/** The gate bell as the trophy card draws it: runes in dragon red; the sash and the banners once earned. */
const bellArt = (banners: boolean, sash: boolean, up: boolean): string => `<svg viewBox="0 0 120 120" aria-hidden="true">
<defs><radialGradient id="tbg" cx="50%" cy="58%" r="55%"><stop offset="0" stop-color="#ff4f3a" stop-opacity="${up ? (sash ? 0.42 : 0.26) : 0.06}"/><stop offset="1" stop-color="#ff4f3a" stop-opacity="0"/></radialGradient>
<linearGradient id="tbm" x1="0" x2="1"><stop offset="0" stop-color="#25302f"/><stop offset=".45" stop-color="#5d7470"/><stop offset="1" stop-color="#1c2423"/></linearGradient></defs>
<rect width="120" height="120" rx="10" fill="#0d0f13"/><circle cx="60" cy="66" r="52" fill="url(#tbg)"/>
<path d="M14 18h92" stroke="#3a3f47" stroke-width="5" stroke-linecap="round"/><path d="M60 18v10" stroke="#6b7079" stroke-width="3"/>
${banners ? '<g fill="#22252b" stroke="#3a3f47"><path d="M20 21h16v42l-8-6-8 6z"/><path d="M84 21h16v42l-8-6-8 6z"/></g><g fill="#ff4f3a"><path d="M25 34a3 3 0 016 0v5h-6z"/><path d="M89 34a3 3 0 016 0v5h-6z"/></g>' : ''}
<g opacity="${up ? 1 : 0.35}"><rect x="50" y="26" width="20" height="6" rx="2" fill="#4a3a2c"/>
<path d="M60 31c-11 0-15 8-16 22-1 12-6 20-14 24v5h60v-5c-8-4-13-12-14-24-1-14-5-22-16-22z" fill="url(#tbm)" stroke="#0b0d10" stroke-width="1.5"/>
<path d="M32 80h56" stroke="#7c948f" stroke-width="3"/>
<g fill="#ff8a6e">${[42, 49, 56, 63, 70, 77].map((x, i) => `<rect x="${x}" y="${58 + (i % 2) * 2}" width="${i % 3 === 0 ? 4 : 2.5}" height="${i % 2 ? 5 : 7}" rx="1"/>`).join('')}</g>
<circle cx="60" cy="88" r="4" fill="#2c3036"/>
${sash ? '<path d="M47 38q13 6 26 0" stroke="#ff4f3a" stroke-width="5" fill="none" stroke-linecap="round"/><path d="M56 40l-6 22 5-3 2 6zM64 40l6 22-5-3-2 6z" fill="#ff4f3a"/>' : ''}</g></svg>`;

/**
 * The bell's trophy: how it looks on the keep and what is left to earn — hung on the keep (once), the old
 * garrison's banners (five times), the red sash (once with nobody falling). For the keep's sheet and the cellars' card.
 */
export const bellTrophyBox = (s: CampaignState, compact = false): HTMLElement => {
  const c = s.expeditions?.cellars ?? { wins: 0, flawless: 0 };
  const look = bellTrophy(s);
  const need = CELLARS.trophy.bannersAt;
  const steps: [boolean, string, string][] = [
    [c.wins >= 1, t('exp.trophy.hung'), t('exp.trophy.hungHow')],
    [c.wins >= need, t('exp.trophy.banners'), t('exp.trophy.bannersHow', { n: Math.min(c.wins, need), need })],
    [c.flawless > 0, t('exp.trophy.sash'), t('exp.trophy.sashHow')],
  ];
  return h(
    `section.trophy${compact ? '.compact' : ''}${look ? '.won' : ''}`,
    { 'data-test': 'bell-trophy' },
    h('div.trophy-art', { html: bellArt(!!look?.banners, !!look?.sash, !!look) }),
    h(
      'div.trophy-body',
      {},
      h('span.label', {}, t('exp.trophy.label')),
      h('h4', {}, t('exp.trophy.title')),
      h('p.small', {}, look ? (c.wins === 1 ? t('exp.trophy.countOne', { f: c.flawless }) : t('exp.trophy.count', { n: c.wins, f: c.flawless })) : t('exp.trophy.notYet')),
      h('ul.trophy-steps', {}, ...steps.map(([ok, name, how]) => h(`li${ok ? '.ok' : ''}`, {}, h('span.trophy-mark', { html: ok ? ICONS.check : ICONS.lock }), h('span', {}, h('b', {}, name), h('span.small', {}, how))))),
    ),
  );
};
